/**
 * VersionComparisonSection Component
 * Displays version comparison information in an accordion
 */

import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { apiClient } from "../../services/apiClient";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";

/** Matches Run AI Assessment contained button on regulation detail header */
const BRAND_CONTAINED_BUTTON_SX = {
  bgcolor: "primary.main !important",
  color: "primary.contrastText !important",
  boxShadow: "none",
  textTransform: "none" as const,
  fontWeight: 500,
  height: "32px",
  px: 1.5,
  "&:hover": {
    bgcolor: "primary.main !important",
    boxShadow: "none",
  },
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit !important",
  },
};

// Git Compare Icon (from lucide-react)
const GitCompareIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle cx="18" cy="18" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="6" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M13 6h3a2 2 0 0 1 2 2v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M11 18H8a2 2 0 0 1-2-2V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </SvgIcon>
);

interface VersionComparisonSectionProps {
  loading: boolean;
  regulationData: Record<string, unknown> | null;
  currentVersion?: string;
  onCompareVersions?: () => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const VersionComparisonSection: React.FC<VersionComparisonSectionProps> = ({
  loading,
  regulationData,
  currentVersion,
  onCompareVersions,
  expanded: expandedProp,
  onExpandedChange,
}) => {
  function formatApiMessage(message: unknown, fallback: string): string {
    if (!message) return fallback;
    if (typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        const parsedMsg = formatApiMessage(parsed, "");
        if (parsedMsg) return parsedMsg;
      } catch {
        // Not JSON - use as plain string
      }
      return message;
    }
    if (Array.isArray(message)) {
      const msgs = message
        .map((o) =>
          o && typeof o === "object" && "msg" in (o as any) ? (o as any).msg : null
        )
        .filter((m): m is string => typeof m === "string" && m.length > 0);
      return msgs.length > 0 ? msgs.join("\n") : fallback;
    }
    if (typeof message === "object" && message !== null) {
      if ("msg" in message) {
        const m = (message as Record<string, unknown>).msg;
        if (typeof m === "string" && m) return m;
      }
    }
    return fallback;
  }

  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const lastCompareFailedRef = useRef(false);
  const [lottieLoaded, setLottieLoaded] = useState(false);
  const [dotLottie, setDotLottie] = useState<any>(null);

  useEffect(() => {
    if (dotLottie) {
      const onReady = () => setLottieLoaded(true);
      dotLottie.addEventListener('ready', onReady);
      dotLottie.addEventListener('load', onReady);
      
      // If it's already loaded when we attach the listener
      if (dotLottie.isReady || dotLottie.isLoaded) {
        setLottieLoaded(true);
      }
      
      return () => {
        dotLottie.removeEventListener('ready', onReady);
        dotLottie.removeEventListener('load', onReady);
      };
    }
  }, [dotLottie]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingActiveRef = useRef(false);
  const canCompare = useHasPrivilege("gri.assessment.comparison.execute");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSelectedVersion, setDialogSelectedVersion] = useState<string>("");
  const [dialogRunning, setDialogRunning] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});
  const comparisonRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      pollingActiveRef.current = false;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, []);

  const handleExpandedChange = () => {
    const newExpanded = !expanded;
    if (onExpandedChange) {
      onExpandedChange(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    // Preload the lottie animation file so it shows up instantly when needed
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'fetch';
    link.href = '/animations/ai-flow.lottie';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleOpenDialog = () => {
    const { left_version } = getVersionsToCompare();
    setDialogSelectedVersion(left_version);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogRunning(false);
  };

  const handleDialogConfirm = () => {
    if (!dialogSelectedVersion) return;

    const normalizeVer = (v: string) => v.startsWith('v') ? v.substring(1) : v;
    const selectedLeft = normalizeVer(dialogSelectedVersion);
    const currentRight = normalizeVer(currentVersion || '');

    if (Array.isArray(comparisonData) && comparisonData.length > 0) {
      const existing = comparisonData.find((item: any) => {
        const itemLeft = normalizeVer(item.left_version || '');
        const itemRight = normalizeVer(item.right_version || '');
        return itemLeft === selectedLeft && itemRight === currentRight;
      });

      if (existing) {
        setDialogOpen(false);
        setDialogRunning(false);
        const key = `${existing.left_version}-${existing.right_version}`;
        setExpandedAccordions(prev => ({ ...prev, [key]: true }));
        setSnackbar({ open: true, message: `Comparison between v${selectedLeft} and v${currentRight} already exists`, severity: 'info' });
        setTimeout(() => {
          comparisonRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
        return;
      }
    }

    setDialogRunning(true);
    handleCompareClick(dialogSelectedVersion);
  };

  const handleCompareClick = async (selectedLeftVersion?: string) => {
    if (!regulationData || comparing || !currentVersion) return;

    setComparing(true);

    const regulation_id = regulationData.regulation_id as string;
    const { left_version: defaultLeftVersion, right_version } = getVersionsToCompare();
    const left_version = selectedLeftVersion || defaultLeftVersion;
    
    pollingActiveRef.current = true;

    const startPolling = async () => {
      if (!pollingActiveRef.current) return;
      
      try {
        const result = await apiClient.post(`/regulations/${regulation_id}/compare`, {
          left_version,
          right_version,
          ...(lastCompareFailedRef.current && { retry_failed: true }),
        });
        
        if (!pollingActiveRef.current) return;

        if (result.status === "success" && result.data?.status === "success") {
          const versionNumber = currentVersion.startsWith('v') ? currentVersion.substring(1) : currentVersion;
          const resultGet = await apiClient.get(`/regulations/${regulationData.regulation_id}/versions/${versionNumber}/comparisons`);
          
          if (!pollingActiveRef.current) return;

          if(resultGet.status === "success" && resultGet.data && resultGet.data.length !== 0){
            setComparisonData(resultGet.data);
          } else {
            setComparisonData(null);
          }
          onCompareVersions?.();
          setComparing(false);
          setDialogRunning(false);
          setDialogOpen(false);
          pollingActiveRef.current = false;
          if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
        } else if (result.status === "success" && result.data?.status === "in_progress") {
          lastCompareFailedRef.current = false
          setComparing(true);
          pollingTimerRef.current = setTimeout(startPolling, 3000);
        } else {
          lastCompareFailedRef.current = true;
          setComparing(false);
          setDialogRunning(false);
          pollingActiveRef.current = false;

          // Same level as message: if outer status is success, use inner message/status
          const errorMsg = result.status === "success" ? result.data?.message : result.message;

          setSnackbar({
            open: true,
            message: formatApiMessage(errorMsg, "Comparison failed"),
            severity: "error",
          });
        }
      } catch (error: any) {
        if (!pollingActiveRef.current) return;
        
        console.error("Error during comparison polling:", error);
        lastCompareFailedRef.current = true;
        setComparing(false);
        setDialogRunning(false);
        pollingActiveRef.current = false;
        setSnackbar({
          open: true,
          message: formatApiMessage(
            error?.response?.data?.message ?? error?.message,
            "An error occurred during comparison"
          ),
          severity: "error",
        });
      }
    };

    startPolling();
  };

  useEffect(() => {
    pollingActiveRef.current = false;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setComparing(false);
    lastCompareFailedRef.current = false;

    const fetchRegulationComparisonData = async () => {
      if(!regulationData || !currentVersion) return;
      try {
        setIsDataFetching(true);
        const versionNumber = currentVersion.startsWith('v') ? currentVersion.substring(1) : currentVersion;
        const result = await apiClient.get(`/regulations/${regulationData.regulation_id}/versions/${versionNumber}/comparisons`);
        if(result.status === "success" && result.data && !(Array.isArray(result.data) && result.data.length === 0)){
          setComparisonData(result.data);
        } else {
          setComparisonData(null);
        }
      } catch (e) {
        console.error("Error fetching initial comparison:", e);
        setComparisonData(null);
        setSnackbar({
          open: true,
          message: formatApiMessage(
            (e as any)?.response?.data?.message ?? (e as any)?.message,
            "Failed to fetch comparison result"
          ),
          severity: "error",
        });
      } finally {
        setIsDataFetching(false);
      }
    }
    fetchRegulationComparisonData();
  }, [regulationData, currentVersion]);

  const getVersionsToCompare = () => {
    const versions = (regulationData?.versions as any[]) || [];
    const normalizedVersions = versions
      .map(v => typeof v === 'string' ? v : v.version)
      .sort((a, b) => {
        const aRaw = a.startsWith('v') ? a.substring(1) : a;
        const bRaw = b.startsWith('v') ? b.substring(1) : b;
        return parseFloat(aRaw) - parseFloat(bRaw);
      });
    
    const rightVersionStr = currentVersion || (regulationData?.version as string) || (normalizedVersions.length > 0 ? normalizedVersions[normalizedVersions.length - 1] : "1.0");
    const rightVersionRaw = rightVersionStr.startsWith('v') ? rightVersionStr.substring(1) : rightVersionStr;
    
    const index = normalizedVersions.findIndex(v => {
      const vRaw = v.startsWith('v') ? v.substring(1) : v;
      return vRaw === rightVersionRaw;
    });

    const isMinVersion = index === 0;
    let leftVersionStr = "";
    
    if (index > 0) {
      leftVersionStr = normalizedVersions[index - 1];
    } else if (normalizedVersions.length > 1) {
      leftVersionStr = normalizedVersions[1];
    } else {
      leftVersionStr = "1.0";
    }

    const rVer = rightVersionStr.startsWith('v') ? rightVersionStr : `v${rightVersionStr}`;
    const lVer = leftVersionStr.startsWith('v') ? leftVersionStr : `v${leftVersionStr}`;
    
    return {
      left_version: leftVersionStr.startsWith('v') ? leftVersionStr.substring(1) : leftVersionStr,
      right_version: rightVersionStr.startsWith('v') ? rightVersionStr.substring(1) : rightVersionStr,
      lVer,
      rVer,
      isMinVersion
    };
  };

  const { lVer, rVer, isMinVersion } = getVersionsToCompare();

  const getChangeTypeBadge = (type: string) => {
    const upper = (type || '').toUpperCase();
    const colorMap: Record<string, { bg: string; color: string }> = {
      MODIFIED: { bg: '#dbeafe', color: '#2563eb' },
      DELETED: { bg: '#fee2e2', color: '#dc2626' },
      REMOVED: { bg: '#fee2e2', color: '#dc2626' },
      ADDED: { bg: '#dcfce7', color: '#16a34a' },
      NEW: { bg: '#dcfce7', color: '#16a34a' },
    };
    const colors = colorMap[upper] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <Chip
        label={upper}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 700,
          fontSize: '0.65rem',
          height: 24,
          letterSpacing: '0.03em',
          borderRadius: '12px',
          '& .MuiChip-label': { px: 1.5 },
        }}
      />
    );
  };

  const renderComparisonContent = (data: any) => {
    if (!data) return null;
    const displayLVer = data.left_version ? (data.left_version.startsWith('v') ? data.left_version : `v${data.left_version}`) : lVer;
    const displayRVer = data.right_version ? (data.right_version.startsWith('v') ? data.right_version : `v${data.right_version}`) : rVer;

    const summaryJson = data.summary_json;
    const sectionResults: Array<{ section?: string; change_type?: string; description?: string }> =
      Array.isArray(summaryJson?.section_results) ? summaryJson.section_results : [];
    const overallSummary: string = summaryJson?.summary ?? "";

    return (
      <Box sx={{ py: 1, px: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, color: '#6b7280' }}>
          <GitCompareIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {displayLVer} → {displayRVer}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
          Summary of Changes Between {displayLVer} and {displayRVer}
        </Typography>

        {overallSummary && (
          <Box
            sx={(theme) => ({
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              borderRadius: "8px",
              p: 2.5,
              mb: 3,
            })}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 18, color: "primary.contrastText" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                Summary Of Overall Changes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                  fontSize: (theme) => theme.typography.caption.fontSize,
                  lineHeight: 1.6,
                  "& em, & i": { fontStyle: "italic !important" },
                  "& strong, & b": { fontWeight: "bold !important" },
                  "& p": { margin: 0, marginBottom: "0.5em" },
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    sectionResults.length === 0
                      ? `${overallSummary} Please read the Details section to find more information about both versions.`
                      : overallSummary,
                }}
              />
            </Box>
          </Box>
        )}

        {sectionResults.length > 0 && (
          <Box>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '190px 140px 1fr',
              gap: 0, px: 2, py: 1.5,
              borderBottom: '1px solid #e5e7eb',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>Section</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>Change Type</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>Description of Change</Typography>
            </Box>
            {sectionResults.map((item: any, idx: number) => {
              const section = item.section || item.title || '-';
              const changeType = item.change_type || item.changeType || item.type || 'MODIFIED';
              const description = item.description || item.summary || item.desc || '-';
              return (
                <Box key={idx} sx={{ borderBottom: '1px solid #f3f4f6' }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '190px 140px 1fr',
                    gap: 0, px: 2, py: 2.5,
                    alignItems: 'center',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.primary', pr: 2, textTransform: 'uppercase', lineHeight: 1.5 }}>
                      {section}
                    </Typography>
                    <Box>{getChangeTypeBadge(changeType)}</Box>
                    <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                      {description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Accordion 
        expanded={expanded} 
        onChange={handleExpandedChange}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px !important",
          boxShadow: "none",
          "&:before": { display: "none" },
          mb: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              my: 1,
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Version Comparison
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Loading version comparison...
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  if (!regulationData) {
    return (
      <Accordion 
        expanded={expanded} 
        onChange={handleExpandedChange}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px !important",
          boxShadow: "none",
          "&:before": { display: "none" },
          mb: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              my: 1,
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Version Comparison
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="error">
            Failed to load version comparison
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box>
      {/* Preload the lottie animation player in the background so it's instant when needed */}
      <Box sx={{ visibility: 'hidden', position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <DotLottieReact src="/animations/ai-flow.lottie" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Version Comparison
        </Typography>
        {comparisonData && canCompare && (() => {
          const versions = (regulationData?.versions as any[]) || [];
          const comparisonCount = Array.isArray(comparisonData) ? comparisonData.length : 1;
          return comparisonCount < versions.length - 1;
        })() && (
          <Button
            variant="contained"
            size="small"
            disabled={comparing || isMinVersion}
            startIcon={comparing ? <CircularProgress size={18} color="inherit" /> : <GitCompareIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenDialog}
            sx={BRAND_CONTAINED_BUTTON_SX}
          >
            Compare Versions
          </Button>
        )}
      </Box>
      <Box sx={{ minHeight: 200, display: "flex", flexDirection: "column" }}>
        {isDataFetching ? (
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
          </Box>
        ) : (comparisonData || comparing) ? (
          <>
            {comparing && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Version Comparison Between {rVer} and {dialogSelectedVersion ? (dialogSelectedVersion.startsWith('v') ? dialogSelectedVersion : `v${dialogSelectedVersion}`) : lVer}
                  </Typography>
                  <CircularProgress size={16} sx={{ color: '#2563eb', ml: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Box sx={{ width: 160, height: 160, mb: 1, position: 'relative' }}>
                    {!lottieLoaded && (
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={32} sx={{ color: '#d1d5db' }} />
                      </Box>
                    )}
                    <DotLottieReact
                      src="/animations/ai-flow.lottie"
                      loop
                      autoplay
                      dotLottieRefCallback={setDotLottie}
                      style={{ width: '100%', height: '100%', opacity: lottieLoaded ? 1 : 0 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    Running Version Comparison...
                  </Typography>
                </Box>
                {comparisonData && <Divider sx={{ mt: 1 }} />}
              </Box>
            )}
            {Array.isArray(comparisonData) && comparisonData.length > 0 ? (
              comparisonData.map((item: any, idx: number) => {
                const key = `${item.left_version}-${item.right_version}`;
                const isExpanded = expandedAccordions[key] !== undefined ? expandedAccordions[key] : idx === 0;
                return (
                  <Accordion
                    key={key}
                    expanded={isExpanded}
                    onChange={() => setExpandedAccordions(prev => ({ ...prev, [key]: !isExpanded }))}
                    ref={(el) => (comparisonRefs.current[key] = el)}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px !important",
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      mb: 2,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        Comparison: v{item.left_version} vs v{item.right_version}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderComparisonContent(item)}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : comparisonData ? (
              renderComparisonContent(comparisonData)
            ) : null}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
              flex: 1
            }}
          >
            <Box
              sx={{
                width: 84,
                height: 54,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GitCompareIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                color: "text.primary",
              }}
            >
              Compare Version Changes
            </Typography>

            <Box sx={{ textAlign: "center", mb: 3 }}>
              {isMinVersion ? (
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Please select a higher version to perform comparison
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", lineHeight: 1.5 }}
                  >
                    {`Generate an AI-powered comparison between ${lVer} and ${rVer}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      lineHeight: 1.5,
                      mt: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    (Compares the primary associated document content. Metadata and Details are excluded.)
                  </Typography>
                </>
              )}
            </Box>

            {canCompare && (
              <Button
                variant="contained"
                size="small"
                disabled={comparing || isMinVersion}
                startIcon={
                  comparing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GitCompareIcon sx={{ fontSize: 20 }} />
                  )
                }
                onClick={handleOpenDialog}
                sx={{
                  ...BRAND_CONTAINED_BUTTON_SX,
                  borderRadius: "8px",
                  height: 40,
                  minHeight: 40,
                  px: 2,
                }}
              >
                Compare Versions
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Version Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick") return;
          handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        {dialogRunning ? (
          <>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 3, py: 2,
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc',
            }}>
              <GitCompareIcon sx={{ fontSize: 20, color: "primary.main" }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Version Comparison
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                  Waiting for results or close to view later
                </Typography>
              </Box>
              <IconButton aria-label="Close" onClick={handleCloseDialog} sx={DIALOG_CLOSE_BUTTON_SX}>
                <CloseIcon />
              </IconButton>
            </Box>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, minHeight: 380 }}>
              <Box sx={{ width: 180, height: 180, mb: 2, position: 'relative' }}>
                {!lottieLoaded && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
                  </Box>
                )}
                <DotLottieReact
                  src="/animations/ai-flow.lottie"
                  loop
                  autoplay
                  dotLottieRefCallback={setDotLottie}
                  style={{ width: '100%', height: '100%', opacity: lottieLoaded ? 1 : 0 }}
                />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Running Version Comparison
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', maxWidth: 380, mb: 4, lineHeight: 1.6 }}>
              Scanning the primary associated documents for textual differences...
              </Typography>

              {/* <Button
                variant="outlined"
                onClick={handleCloseDialog}
                sx={{
                  textTransform: 'none', borderRadius: '6px',
                  color: 'primary.main', borderColor: 'primary.main',
                  px: 3, py: 0.75,
                  '&:hover': { backgroundColor: '#eff6ff', borderColor: 'primary.dark' },
                }}
              >
                View Results Later
              </Button> */}
            </DialogContent>
          </>
        ) : (
          <>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              px: 3, pt: 3, pb: 2,
            }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Select Versions To Compare
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5, display: 'block' }}>
                  Select exactly one version to identify discrepancies.
                </Typography>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={handleCloseDialog}
                sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: -0.5 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
              {(() => {
                const versions = (regulationData?.versions as any[]) || [];
                const currentRaw = currentVersion
                  ? (currentVersion.startsWith('v') ? currentVersion.substring(1) : currentVersion)
                  : "";

                const versionItems = versions
                  .map(v => {
                    const isObj = typeof v === 'object' && v !== null;
                    const verStr = isObj ? String(v.version) : String(v);
                    const raw = verStr.startsWith('v') ? verStr.substring(1) : verStr;
                    return {
                      raw,
                      display: verStr.startsWith('v') ? verStr : `v${verStr}`,
                      release_date: isObj ? v.release_date : undefined,
                      summary: isObj ? (v.summary || v.description) : undefined,
                      isBase: raw === currentRaw,
                      isPrevious: parseFloat(raw) < parseFloat(currentRaw),
                    };
                  })
                  .sort((a, b) => parseFloat(b.raw) - parseFloat(a.raw));

                if (versionItems.length === 0) {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                      <GitCompareIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                      <Typography sx={{ color: '#6b7280', fontWeight: 500 }}>
                        No versions available for comparison
                      </Typography>
                    </Box>
                  );
                }

                const baseItems = versionItems.filter(v => v.isBase);
                const previousItems = versionItems.filter(v => v.isPrevious);

                const formatDate = (dateStr: string | undefined) => {
                  if (!dateStr) return null;
                  try {
                    const d = new Date(dateStr);
                    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
                  } catch { return dateStr; }
                };

                const renderCard = (item: typeof versionItems[0]) => {
                  const isSelected = !item.isBase && dialogSelectedVersion === item.raw;
                  const isSelectable = item.isPrevious;

                  return (
                    <Box
                      key={item.raw}
                      onClick={() => { if (isSelectable) setDialogSelectedVersion(item.raw); }}
                      sx={{
                        border: isSelected ? "2px solid" : "1px solid",
                        borderColor: isSelected ? "primary.main" : "#e0e0e0",
                        borderRadius: "8px",
                        px: 2.5,
                        py: 2,
                        mb: 1.5,
                        cursor: isSelectable ? "pointer" : "default",
                        backgroundColor: item.isBase ? "#f9fafb" : "white",
                        transition: "border-color 0.15s",
                        "&:hover": isSelectable
                          ? { borderColor: "primary.main" }
                          : {},
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 70, flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            lineHeight: 1,
                            mb: 0.5,
                            display: "block",
                          }}
                        >
                          Version
                        </Typography>
                        <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                          {item.display}
                        </Typography>
                      </Box>

                      {item.release_date && (
                        <Box sx={{ minWidth: 90, flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500, lineHeight: 1, mb: 0.5, display: "block" }}>Release Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", lineHeight: 1.2 }}>
                            {formatDate(item.release_date)}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        sx={(theme) => ({
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "4px",
                          backgroundColor: item.isBase
                            ? alpha(theme.palette.primary.main, 0.12)
                            : "#F2F2F2",
                          flexShrink: 0,
                        })}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: item.isBase ? "primary.main" : "#566166",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.isBase ? "Base Version" : "Previous Version"}
                        </Typography>
                      </Box>

                      {item.summary && (
                        <Typography variant="caption" sx={{ color: "#6b7280", flex: 1, lineHeight: 1.5 }} noWrap>
                          {item.summary}
                        </Typography>
                      )}

                      {isSelectable && (
                        <Box sx={{ ml: "auto", flexShrink: 0, display: "flex", alignItems: "center" }}>
                          {isSelected ? (
                            <CheckCircleIcon sx={{ color: "primary.main", fontSize: 24 }} />
                          ) : (
                            <Box sx={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid #d1d5db" }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                };

                return (
                  <>
                    {baseItems.map(renderCard)}
                    {baseItems.length > 0 && previousItems.length > 0 && <Box sx={{ mb: 0.5 }} />}
                    {previousItems.map(renderCard)}
                  </>
                );
              })()}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: "6px", px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDialogConfirm}
                variant="contained"
                disabled={!dialogSelectedVersion}
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  px: 3,
                  fontWeight: 600,
                }}
              >
                Run Compare Versions
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
